import React, { useEffect, useMemo, useState } from "react";
import { ShoppingCart, MessageSquareText, ArrowRight, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../utils/axiosInstance";
import API_BASE_URL from "../../../services/api";

const ymd = (d) => {
  const dt = new Date(d);
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

// small helper for nice time
const timeOnly = (iso) => {
  if (!iso) return "";
  const dt = new Date(iso);
  return dt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
};

export default function TodayActivity() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [cartData, setCartData] = useState({ total: 0, list: [] });
  const [enqData, setEnqData] = useState({ total: 0, list: [], statusCount: {} });
  const [err, setErr] = useState("");

  const today = useMemo(() => new Date(), []);
  const todayStr = useMemo(() => ymd(today), [today]);

  const fetchToday = async () => {
    setLoading(true);
    setErr("");

    try {
      // ✅ carts: use your existing endpoint with fromDate/toDate = today
      const cartsReq = axiosInstance.get(`${API_BASE_URL}/api/carts`, {
        params: {
          page: 1,
          limit: 5,
          fromDate: todayStr,
          toDate: todayStr,
        },
      });

      // ✅ enquiry: you must add date filter in backend (shown below)
      const enqReq = axiosInstance.get(`${API_BASE_URL}/api/enquiry`, {
        params: {
          page: 1,
          limit: 5,
          fromDate: todayStr,
          toDate: todayStr,
          sortBy: "createdAt",
          order: "desc",
        },
      });

      const [cartsRes, enqRes] = await Promise.all([cartsReq, enqReq]);

      // CARTS
      if (cartsRes?.data?.success) {
        const list = cartsRes.data?.data || [];
        const meta = cartsRes.data?.meta || {};
        setCartData({
          total: meta?.total ?? list.length,
          list,
        });
      } else {
        setCartData({ total: 0, list: [] });
      }

      // ENQUIRY
      if (enqRes?.data?.success) {
        const items = enqRes.data?.data?.items || [];
        const total = enqRes.data?.data?.total || 0;

        const sc = items.reduce((acc, it) => {
          const s = it.status || "NEW";
          acc[s] = (acc[s] || 0) + 1;
          return acc;
        }, {});

        setEnqData({
          total,
          list: items,
          statusCount: sc,
        });
      } else {
        setEnqData({ total: 0, list: [], statusCount: {} });
      }
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to load today activity");
      setCartData({ total: 0, list: [] });
      setEnqData({ total: 0, list: [], statusCount: {} });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchToday();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const Card = ({ title, icon: Icon, total, onViewAll, children }) => (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold text-gray-900">{title}</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">
            {loading ? <span className="text-gray-300">—</span> : total}
          </p>
          <p className="text-[11px] text-gray-500">Today</p>
        </div>

        <div className="flex items-center gap-2">
          <div className="rounded-lg bg-gray-50 p-2">
            <Icon size={18} className="text-gray-700" />
          </div>

          <button
            onClick={onViewAll}
            className="inline-flex items-center gap-1 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-gray-700 hover:bg-gray-50"
          >
            View All <ArrowRight size={12} />
          </button>
        </div>
      </div>

      <div className="mt-3">{children}</div>
    </div>
  );

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Today Activity</h3>
            <p className="text-xs text-gray-600 mt-0.5">Live snapshot of today carts and enquiries</p>
          </div>

          <button
            onClick={fetchToday}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : "↻"}
            Refresh
          </button>
        </div>

        {err && (
          <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {err}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 flex-1 overflow-auto">
        <div className="grid grid-cols-1 gap-4">
          {/* Carts */}
          <Card
            title="Active Carts"
            icon={ShoppingCart}
            total={cartData.total}
            onViewAll={() => navigate("/cart-details")}
          >
            {loading ? (
              <div className="text-xs text-gray-400">Loading...</div>
            ) : cartData.list.length === 0 ? (
              <div className="text-xs text-gray-500">No carts created today</div>
            ) : (
              <div className="space-y-2">
                {cartData.list.map((c) => (
                  <div
                    key={c.id}
                    className="flex items-center justify-between rounded-md bg-gray-50 px-3 py-2"
                  >
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-900 truncate">
                        {c.patient?.fullName || "Unknown Patient"}
                      </p>
                      <p className="text-[11px] text-gray-500">
                        #{c.id} • {c.cartItems?.length || 0} item(s)
                      </p>
                    </div>
                    <p className="text-[11px] text-gray-500">{timeOnly(c.createdAt)}</p>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Enquiries */}
          <Card
            title="Enquiries"
            icon={MessageSquareText}
            total={enqData.total}
            onViewAll={() => navigate("/enquiry")}
          >
            {loading ? (
              <div className="text-xs text-gray-400">Loading...</div>
            ) : (
              <>
                {/* small status pills */}
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {["NEW", "IN_PROGRESS", "RESOLVED", "CLOSED"].map((s) => (
                    <span
                      key={s}
                      className="rounded-full border border-gray-200 bg-white px-2 py-0.5 text-[10px] text-gray-700"
                    >
                      {s.replace("_", " ")}:{" "}
                      <span className="font-semibold">{enqData.statusCount[s] || 0}</span>
                    </span>
                  ))}
                </div>

                {enqData.list.length === 0 ? (
                  <div className="text-xs text-gray-500">No enquiries created today</div>
                ) : (
                  <div className="space-y-2">
                    {enqData.list.map((e) => (
                      <div
                        key={e.id}
                        className="flex items-center justify-between rounded-md bg-gray-50 px-3 py-2"
                      >
                        <div className="min-w-0">
                          <p className="text-xs font-medium text-gray-900 truncate">
                            {e.name || "Unnamed"}
                          </p>
                          <p className="text-[11px] text-gray-500">
                            #{e.id} • {e.number || "N/A"} • {e.status || "NEW"}
                          </p>
                        </div>
                        <p className="text-[11px] text-gray-500">{timeOnly(e.createdAt)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
